import { useEffect, useState } from 'react';
import axios from 'axios';
import { contractorToken } from './contractorAuth';
import { storedViewAsContractorId, storedViewAsUserId } from './viewAs';

/**
 * Documentation (migration 083): the calls the screens make, in one place.
 *
 * WHO IS ASKING. A member of staff through the interceptor (lib/apiClient),
 * like every other page; a contractor by their portal token; and an
 * administrator looking through a contractor's portal names them with
 * as=c:<id>, exactly as the to-dos and Links do - so the docs a preview shows
 * are the ones that contractor would see, and the server records the looking
 * as nobody's.
 *
 * The server decides everything (lib/docs-access.js); this only asks.
 */
const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const viewingContractor = () => (!contractorToken() && storedViewAsContractorId()) || null;

/** The axios config for a docs call: the portal token, and as=c:<id> when previewing. */
export function docsConfig(extra = {}) {
  const token = contractorToken();
  const params = { ...(extra.params || {}) };
  const as = viewingContractor();
  if (as) params.as = `c:${as}`;
  return {
    ...extra,
    params,
    headers: { ...(extra.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) }
  };
}

export const docsApi = {
  get: (path, params) => axios.get(`${API}${path}`, docsConfig({ params })).then((r) => r.data),
  post: (path, body) => axios.post(`${API}${path}`, body, docsConfig()).then((r) => r.data),
  put: (path, body) => axios.put(`${API}${path}`, body, docsConfig()).then((r) => r.data),
  patch: (path, body) => axios.patch(`${API}${path}`, body, docsConfig()).then((r) => r.data),
  del: (path) => axios.delete(`${API}${path}`, docsConfig()).then((r) => r.data),
  upload: (articleId, file) => {
    const fd = new FormData();
    fd.append('file', file, file.name || 'pasted.png');
    return axios.post(`${API}/docs/articles/${articleId}/files`, fd, docsConfig()).then((r) => r.data);
  },
  /** A file's bytes as a blob (an <img> cannot send the token itself). */
  blob: (fileId) => axios.get(`${API}/docs/files/${fileId}`, docsConfig({ responseType: 'blob' })).then((r) => r.data)
};

/** The server's sentence for a failed call. */
export const errorOf = (e, fallback = 'Something went wrong') =>
  e?.response?.data?.error || (e?.response ? fallback : 'Could not reach the server');

/* ---------------------------------------------------- may I, at all? */

let cached = null;
let inflight = null;
const listeners = new Set();
const whoKey = () => `${contractorToken() ? 't' : 's'}:${viewingContractor() || ''}:${storedViewAsUserId() || ''}`;

/**
 * GET /docs/me, shared: { ready, can_read, can_author, can_manage,
 * can_create_space, is_admin, reviews_due }. Null until known. The navbar
 * uses it to offer "Docs" only to somebody with something to read - a
 * menu item onto an empty page is a broken link - and an administrator
 * always (there is always something to set up).
 */
export function fetchDocsMe({ force = false } = {}) {
  const who = whoKey();
  if (!force && cached && cached.for === who) return Promise.resolve(cached);
  if (!force && inflight) return inflight;
  inflight = docsApi.get('/docs/me')
    .catch((e) => ({ ready: false, failed: true, switched_off: e?.response?.data?.switched_off === true }))
    .then((v) => {
      cached = { ...v, for: who };
      inflight = null;
      listeners.forEach((fn) => { try { fn(cached); } catch { /* keep going */ } });
      return cached;
    });
  return inflight;
}

export const refreshDocsMe = () => fetchDocsMe({ force: true });

export function useDocsMe(enabled = true) {
  const [v, setV] = useState(cached && cached.for === whoKey() ? cached : null);
  useEffect(() => {
    if (!enabled) return undefined;
    let live = true;
    const on = (x) => { if (live) setV(x); };
    listeners.add(on);
    fetchDocsMe().then(on);
    return () => { live = false; listeners.delete(on); };
  }, [enabled]);
  return enabled ? v : null;
}

/* ---------------------------------------------------- words */

export const TYPE_LABEL = { howto: 'How-to', runbook: 'Runbook', workflow: 'Workflow', reference: 'Reference', faq: 'FAQ' };
export const TYPES = ['howto', 'runbook', 'workflow', 'reference', 'faq'];
export const TYPE_BLURB = {
  howto: 'Steps to get one thing done.',
  runbook: 'A fix to follow while doing it - steps a reader ticks off.',
  workflow: 'Who does what, and when - drawn as a flow.',
  reference: 'Facts to look up: settings, addresses, contacts.',
  faq: 'Questions people ask, with the answers.'
};
export const LEVEL_LABEL = { deny: 'No access', none: 'No access', view: 'View', comment: 'Comment', edit: 'Edit', manage: 'Manage' };
export const LEVEL_BLURB = {
  deny: 'Shut out here, even if something above lets them in.',
  view: 'Read it.',
  comment: 'Read it and comment.',
  edit: 'Write, publish and attach files.',
  manage: 'Edit, and decide who can see it.'
};

/** A starting text for each kind of article. */
export const TEMPLATE = {
  howto: '## Before you start\n\n- What you need\n\n## Steps\n\n1. First\n2. Then\n\n## Check it worked\n\n',
  runbook: '> [!NOTE]\n> When to use this runbook, in one sentence.\n\n## Before you start\n\n- [ ] Access you need\n- [ ] Tools\n\n## Steps\n\n1. [ ] First step\n2. [ ] Second step\n3. [ ] Confirm the fix\n\n## If it does not work\n\n',
  workflow: 'What this workflow is for.\n\n```flow\nRequest received | Helpdesk | Same day\nCheck and approve | IT staff | Within 1 day\nDo the work | Contractor | As agreed\nClose the ticket | IT staff | When confirmed\n```\n\n## Notes\n\n',
  reference: '| Item | Value |\n| --- | --- |\n| Example | Value |\n\n> [!WARNING]\n> No passwords here - write where they are kept instead.\n',
  faq: '## Question one?\n\nThe answer.\n\n## Question two?\n\nThe answer.\n'
};

export const when = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days ago`;
  return d.toLocaleDateString('en-GB', { timeZone: 'Europe/Malta', day: 'numeric', month: 'short', year: 'numeric' });
};

export const fullDate = (iso) => (iso ? new Date(iso).toLocaleString('en-GB', { timeZone: 'Europe/Malta',
  day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
}) : '');
